import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaInbox,
} from "react-icons/fa";
import {
  MODULE_PAGE_SHELL_STYLE,
  TASK_PAGE_SHELL_CLASS,
} from "../../TaskManagement/workspaceConstants";
import Paginations from "../../GenericComponents/Pagination";
import CommissionInstallmentsSearchFilters from "./CommissionInstallmentsSearchFilters";
import CommissionInstallmentsQueueTabs from "./CommissionInstallmentsQueueTabs";
import CommissionsTableSkeleton from "./CommissionsTableSkeleton";
import { formatCurrency } from "./detail/commissionDetailConstants";
import {
  DUE_FILTER_OPTIONS,
  formatDueDateLabel,
  resolveDueUrgencyTone,
} from "./commissionInstallmentsQueueUtils";
import { useCommissionInstallmentsQueue } from "./useCommissionInstallmentsQueue";

import "../../../styles/tailwind.css";
import "./commissions-responsive.css";

const DEFAULT_PER_PAGE = 10;

const TABLE_CELL_CLASS = "px-3 py-2.5";
const TABLE_HEAD_CLASS =
  "border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500";

const INSTALLMENT_TABLE_COLUMNS = [
  { key: "dueDate", title: "Due date" },
  { key: "client", title: "Client name" },
  { key: "application", title: "Application name" },
  { key: "feeDue", title: "Fee due" },
  { key: "commissionDue", title: "Commission due" },
  { key: "open", title: "Open" },
];

function useDebouncedValue(value, delayMs = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function formatClientDisplay(row) {
  const name = String(row.clientName || row.studentName || "").trim();
  if (name) return name;
  return row.clientId ? `#${row.clientId}` : "—";
}

function formatApplicationDisplay(row) {
  const name = String(row.applicationName || "").trim();
  if (name) return name;
  return row.applicationId ? `#${row.applicationId}` : "—";
}

function CommissionInstallmentsQueuePage() {
  const [queueType, setQueueType] = useState("commission");
  const [dueFilter, setDueFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const debouncedSearch = useDebouncedValue(searchInput, 500);

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const {
    loading,
    error,
    rows,
    pagination,
    queueMeta,
  } = useCommissionInstallmentsQueue({
    page: currentPage,
    perPage,
    dueFilter,
    dueFrom: customStartDate,
    dueTo: customEndDate,
    search: debouncedSearch,
    queueType,
  });

  const isBonusTab = queueType === "bonus";
  const showFeeColumn = !isBonusTab;
  const amountDueLabel = isBonusTab ? "Bonus due" : "Commission due";

  const tabCounts = useMemo(
    () => ({
      awaitingCommission: queueMeta.awaitingCommission,
      awaitingBonus: queueMeta.awaitingBonus,
    }),
    [queueMeta],
  );

  const summaryAwaitingTotal = isBonusTab
    ? queueMeta.awaitingBonus
    : queueMeta.awaitingCommission;

  const filterSummaryLabel = useMemo(() => {
    const activeFilterLabel =
      DUE_FILTER_OPTIONS.find((o) => o.id === dueFilter)?.label || "All";
    if (dueFilter !== "custom") return activeFilterLabel;
    if (!customStartDate && !customEndDate) return "Custom Date";
    const parts = [];
    if (customStartDate) parts.push(`from ${customStartDate}`);
    if (customEndDate) parts.push(`to ${customEndDate}`);
    return `Custom ${parts.join(" ")}`;
  }, [dueFilter, customStartDate, customEndDate]);

  const hasActiveFilters = useMemo(
    () =>
      dueFilter !== "all" ||
      Boolean(customStartDate) ||
      Boolean(customEndDate) ||
      Boolean(String(searchInput || "").trim()),
    [dueFilter, customStartDate, customEndDate, searchInput],
  );

  const handleClearAll = useCallback(() => {
    setDueFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setSearchInput("");
    setCurrentPage(1);
  }, []);

  const handlePaginationCallback = useCallback((page, nextPerPage) => {
    if (nextPerPage && nextPerPage !== "") {
      setPerPage(Number(nextPerPage) || DEFAULT_PER_PAGE);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  }, []);

  const showingCount = rows.length;

  return (
    <div className="tailwind-container">
      <div
        className={`${TASK_PAGE_SHELL_CLASS} cms-commissions-shell`}
        style={MODULE_PAGE_SHELL_STYLE}
        aria-busy={loading}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-bold leading-tight text-[#35314d] sm:text-2xl">
              Installments
            </h1>
            <p className="mt-0.5 text-[13px] leading-snug text-gray-500">
              Unpaid commission and bonus installments — open detail to manage
              payment
            </p>
          </div>
        </div>

        <div className="mt-3">
          <CommissionInstallmentsQueueTabs
            value={queueType}
            counts={tabCounts}
            onChange={(next) => {
              resetToFirstPage();
              setQueueType(next);
            }}
          />
        </div>

        <div className="mt-3">
          <CommissionInstallmentsSearchFilters
            dueFilter={dueFilter}
            onDueFilterChange={(value) => {
              resetToFirstPage();
              setDueFilter(value);
            }}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onCustomStartDateChange={(value) => {
              resetToFirstPage();
              setCustomStartDate(value);
            }}
            onCustomEndDateChange={(value) => {
              resetToFirstPage();
              setCustomEndDate(value);
            }}
            searchTerm={searchInput}
            onSearchChange={setSearchInput}
            onSearchClear={() => setSearchInput("")}
            onClearAll={handleClearAll}
            hasActiveFilters={hasActiveFilters}
            resultCount={pagination.total}
            totalAwaiting={summaryAwaitingTotal}
            loading={loading}
          />
        </div>

        {error ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-800">
            {error}
          </div>
        ) : null}

        <div className="mt-3 overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <CommissionsTableSkeleton
              rows={10}
              embedded
              columns={INSTALLMENT_TABLE_COLUMNS}
            />
          ) : (
            <div className="cms-commissions-table-wrap overflow-x-auto">
              <table className="w-full min-w-[40rem] text-left text-[13px]">
                <thead className={TABLE_HEAD_CLASS}>
                  <tr>
                    <th className={`whitespace-nowrap ${TABLE_CELL_CLASS}`}>
                      Due date
                    </th>
                    <th className={`whitespace-nowrap ${TABLE_CELL_CLASS}`}>
                      Client name
                    </th>
                    <th className={`whitespace-nowrap ${TABLE_CELL_CLASS}`}>
                      Application name
                    </th>
                    {showFeeColumn ? (
                      <th
                        className={`whitespace-nowrap ${TABLE_CELL_CLASS} text-right`}
                      >
                        Fee due
                      </th>
                    ) : null}
                    <th
                      className={`whitespace-nowrap ${TABLE_CELL_CLASS} text-right`}
                    >
                      {amountDueLabel}
                    </th>
                    <th
                      className={`whitespace-nowrap ${TABLE_CELL_CLASS} text-right`}
                    >
                      Open
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {rows.length > 0 ? (
                    rows.map((row) => {
                      const urgency = resolveDueUrgencyTone(row.dueDate);
                      return (
                        <tr
                          key={`${row.installmentKind}-${row.planId}-${row.id}`}
                          className="transition hover:bg-[#F8FAFC]"
                        >
                          <td className={TABLE_CELL_CLASS}>
                            <div className="flex items-center gap-2">
                              <FaCalendarAlt
                                className={
                                  urgency === "red"
                                    ? "text-red-500"
                                    : urgency === "amber"
                                      ? "text-amber-500"
                                      : "text-gray-400"
                                }
                                size={12}
                              />
                              <span className="font-medium tabular-nums text-gray-800">
                                {formatDueDateLabel(row.dueDate)}
                              </span>
                            </div>
                          </td>
                          <td className={`${TABLE_CELL_CLASS} font-medium text-gray-900`}>
                            {formatClientDisplay(row)}
                          </td>
                          <td className={`${TABLE_CELL_CLASS} text-gray-800`}>
                            {formatApplicationDisplay(row)}
                          </td>
                          {showFeeColumn ? (
                            <td className={`${TABLE_CELL_CLASS} text-end tabular-nums font-medium text-gray-800`}>
                              {formatCurrency(row.feeDue, row.currency)}
                            </td>
                          ) : null}
                          <td className={`${TABLE_CELL_CLASS} text-end tabular-nums font-semibold text-[#007BFF]`}>
                            {formatCurrency(row.commissionDue, row.currency)}
                          </td>
                          <td className={`${TABLE_CELL_CLASS} text-right`}>
                            {row.detailPath ? (
                              <Link
                                to={row.detailPath}
                                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#007BFF] hover:underline"
                              >
                                Open
                                <FaExternalLinkAlt size={10} />
                              </Link>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={showFeeColumn ? 6 : 5}
                        className="px-3 py-10 text-center"
                      >
                        <div className="mx-auto flex max-w-sm flex-col items-center">
                          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
                            <FaInbox size={16} className="text-gray-400" />
                          </div>
                          <p className="text-[15px] font-semibold text-gray-900">
                            No installments found
                          </p>
                          <p className="mt-0.5 text-[13px] text-gray-500">
                            No results for &ldquo;{filterSummaryLabel}&rdquo;
                            {debouncedSearch ? " with your search." : "."} Try
                            adjusting filters.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!loading && pagination.total > 0 && (
            <div className="border-t border-gray-100 px-2 py-1">
              <Paginations
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.perPage}
                lastPage={pagination.lastPage}
                onPageChange={handlePaginationCallback}
                totalItems={pagination.total}
                perItem={showingCount}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CommissionInstallmentsQueuePage;
